import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';

/**
 * Standard "Actualizar" button. Visually distinct from the back button:
 * filled emerald accent so the user clearly sees there's a refresh CTA.
 *
 * Handles spinning state automatically while the async onClick is running.
 *
 *   <RefreshButton onClick={fetchData} />
 *   <RefreshButton onClick={fetchData} label="Refrescar" />
 *   <RefreshButton onClick={fetchData} loading={external} />
 */
export default function RefreshButton({
  onClick,
  label = 'Actualizar',
  className = '',
  size = 'sm',
  variant = 'solid', // 'solid' | 'subtle'
  loading: loadingProp,
  testId = 'refresh-btn',
  showLabelOnMobile = false,
}) {
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = loadingProp ?? internalLoading;

  const handle = async () => {
    if (!onClick || loading) return;
    try {
      setInternalLoading(true);
      await onClick();
    } finally {
      setInternalLoading(false);
    }
  };

  const styles = {
    solid: cn(
      'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white',
      'border border-emerald-500/60 hover:border-emerald-400',
      'shadow-sm focus-visible:ring-2 focus-visible:ring-emerald-300',
    ),
    subtle: cn(
      'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300',
      'border border-emerald-500/40 hover:border-emerald-400',
      'focus-visible:ring-2 focus-visible:ring-emerald-500',
    ),
  };

  return (
    <Button
      type="button"
      onClick={handle}
      disabled={loading}
      size={size}
      className={cn(styles[variant] || styles.solid, 'transition-colors', className)}
      data-testid={testId}
      aria-label={label}
    >
      <RefreshCw className={cn('w-4 h-4 sm:mr-2', loading && 'animate-spin')} />
      <span className={showLabelOnMobile ? '' : 'hidden sm:inline'}>
        {loading ? 'Actualizando...' : label}
      </span>
    </Button>
  );
}
